/* eslint-disable max-len, object-curly-newline, react/jsx-one-expression-per-line, newline-per-chained-call, react/jsx-wrap-multilines, implicit-arrow-linebreak, function-paren-newline, operator-linebreak, react/jsx-curly-newline */
import React, { useContext, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import NavigationLayout from '../../components/NavigationLayout';
import CustomTable from '../../components/table/CustomTable';
import DateRangePicker from '../../components/dateRangePicker';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

/*
 * Chart Abstraction — Team productivity & QA reporting. Backed by
 * /chart-abstraction/admin/productivity over a selectable date range. Case
 * counts are abstractor submissions (not QA completions); shows abstraction
 * time, acceptance vs denial, QA scores, cases-per-day and common error types,
 * with a CSV/Excel export for management.
 */

const pad = (n) => String(n).padStart(2, '0');
const fmt = (s) => `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
const initials = (name) => (name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const csvCell = (c) => `"${String(c === null || c === undefined ? '' : c).replace(/"/g, '""')}"`;

const card = { background: '#fff', border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 12, boxShadow: '0 1px 2px rgba(14,30,64,0.04)' };
const cardHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 8px' };
const cardTitle = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' };
const subS = { fontSize: 12.5, color: 'var(--dark-3, #546077)', marginTop: 2 };
const pill = (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', fontSize: 12.5, fontWeight: 600, borderRadius: 20, padding: '3px 11px', background: bg, color: fg, whiteSpace: 'nowrap' });
const avatar = (name) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', background: 'var(--blue-7, #D8E6FC)', color: 'var(--primary, #213D69)', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>{initials(name)}</span>
);
const scoreColor = (s) => {
  if (s >= 90) return 'var(--green-1, #256D23)';
  if (s >= 70) return '#B3650F';
  return 'var(--red-2, #CC4525)';
};

const CA_TABLE_HEADERS = [
  { label: 'Member', colSpan: 2 }, { label: 'Role' }, { label: 'Cases' }, { label: 'Avg time' },
  { label: 'Accepted' }, { label: 'Denied' }, { label: 'Acceptance' }, { label: 'Avg score' }, { label: 'Pended' },
];

const STAT_DEFS = [
  { key: 'cases', label: 'Total cases' },
  { key: 'avgSeconds', label: 'Avg time / case', time: true },
  { key: 'acceptanceRate', label: 'Acceptance rate', pct: true },
  { key: 'denialRate', label: 'Denial rate', pct: true },
  { key: 'avgScore', label: 'Avg QA score', pct: true, score: true },
];

function ChartAbstractionProductivity() {
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const workspaceId = activeWorkspace?.id;

  const [productivity, setProductivity] = useState({ members: [], errorTypes: [], daily: [], totals: {} });
  const [range, setRange] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProductivity = async (r, showSpinner = false) => {
    if (!workspaceId) return;
    if (showSpinner) setLoading(true);
    const from = r && r[0] ? toYmd(r[0]) : '';
    const to = r && r[1] ? toYmd(r[1]) : '';
    const res = await sendRequest({ method: 'get', url: API.CHART_ABSTRACTION.productivity(workspaceId, from, to) });
    if (res.success) {
      setProductivity(res.data);
    } else {
      displayNotification({ message: res.data?.error || 'Failed to load productivity data', severity: 'error' });
    }
    if (showSpinner) setLoading(false);
  };

  useEffect(() => {
    if (workspaceId) fetchProductivity(range, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, range]);

  const exportCsv = () => {
    const t = productivity.totals || {};
    const sections = [];
    sections.push(['Team productivity']);
    sections.push(['Member', 'Role', 'Cases', 'Avg time', 'Accepted', 'Denied', 'Acceptance %', 'Avg score', 'Pended']);
    (productivity.members || []).forEach((m) => sections.push([
      m.name, m.role === 'QA' ? 'QA' : 'Abstraction Rep', m.charts,
      m.avgSeconds == null ? '' : fmt(m.avgSeconds), m.accepted ?? '', m.denied ?? '',
      m.acceptanceRate == null ? '' : `${m.acceptanceRate}%`, m.avgScore == null ? '' : `${m.avgScore}%`,
      m.pended == null ? '' : m.pended,
    ]));
    sections.push([]);
    sections.push(['Totals', '', t.cases ?? 0, t.avgSeconds == null ? '' : fmt(t.avgSeconds), t.accepted ?? 0, t.denied ?? 0, t.acceptanceRate == null ? '' : `${t.acceptanceRate}%`, t.avgScore == null ? '' : `${t.avgScore}%`, '']);
    sections.push([]);
    sections.push(['Common error types']);
    sections.push(['Error type', 'Count']);
    (productivity.errorTypes || []).forEach((e) => sections.push([e.name, e.count]));
    sections.push([]);
    sections.push(['Cases per day']);
    sections.push(['Date', 'Cases']);
    (productivity.daily || []).forEach((d) => sections.push([d.date, d.cases]));

    const csv = sections.map((r) => r.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'chart-abstraction-productivity.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const totals = productivity.totals || {};
  const statValue = (k) => {
    const v = totals[k.key];
    if (k.time) return v == null ? '—' : fmt(v);
    if (k.pct) return v == null ? '—' : `${v}%`;
    return String(v == null ? 0 : v);
  };

  const errorTypes = productivity.errorTypes || [];
  const maxErr = errorTypes.reduce((m, e) => Math.max(m, e.count), 0) || 1;
  const daily = productivity.daily || [];
  const maxDay = daily.reduce((m, d) => Math.max(m, d.cases), 0) || 1;

  const cell = (v) => <span style={{ fontSize: 14, color: 'var(--dark-2, #293855)' }}>{v}</span>;
  const items = (productivity.members || []).map((m) => ({
    member: <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>{avatar(m.name)}<span style={{ fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>{m.name}</span></div>,
    role: <span style={pill(m.role === 'QA' ? '#F3E8FF' : '#EAF1FF', m.role === 'QA' ? '#7c3aed' : '#2563eb')}>{m.role === 'QA' ? 'QA' : 'Abstraction Rep'}</span>,
    charts: <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>{m.charts}</span>,
    avg: cell(m.avgSeconds == null ? '—' : fmt(m.avgSeconds)),
    accepted: cell(m.accepted == null ? '—' : m.accepted),
    denied: cell(m.denied == null ? '—' : m.denied),
    acceptance: cell(m.acceptanceRate == null ? '—' : `${m.acceptanceRate}%`),
    avgScore: m.avgScore == null ? cell('—') : <span style={{ fontSize: 14, fontWeight: 600, color: scoreColor(m.avgScore) }}>{`${m.avgScore}%`}</span>,
    pended: cell(m.pended == null ? '—' : m.pended),
  }));

  return (
    <NavigationLayout title="Team Productivity" subtitle="Chart abstraction output and QA quality across your team">
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><CircularProgress size={30} /></div>
      ) : (
        <div style={{ marginTop: 24, fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Range + export */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 14, color: 'var(--dark-3, #546077)' }}>Case counts are based on abstractor submissions across the selected period.</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DateRangePicker value={range} onChange={setRange} placement="bottomEnd" />
              <Button size="small" variant="outlined" startIcon={<FileDownloadOutlined />} onClick={exportCsv} sx={{ textTransform: 'none', color: 'var(--dark-2, #293855)', borderColor: 'var(--dark-6, #E0E1E6)' }}>Export</Button>
            </div>
          </div>

          {/* Totals */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            {STAT_DEFS.map((k) => (
              <div key={k.key} style={{ ...card, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--dark-3, #546077)', marginBottom: 8 }}>{k.label}</div>
                <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, color: k.score && totals[k.key] != null ? scoreColor(totals[k.key]) : 'var(--dark-1, #0E1E40)' }}>{statValue(k)}</div>
              </div>
            ))}
          </div>

          {/* Team productivity table */}
          <div style={card}>
            <div style={{ ...cardHead, paddingBottom: 8 }}>
              <span style={cardTitle}>Team productivity</span>
            </div>
            <div style={{ padding: '0 8px 8px' }}>
              <CustomTable headers={CA_TABLE_HEADERS} items={items} noItemText="No activity for this period." />
            </div>
          </div>

          {/* Cases per day + common error types */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ ...card, flex: 1, minWidth: 340, padding: '16px 20px 20px' }}>
              <div style={{ ...cardTitle, marginBottom: 14 }}><CalendarMonthOutlined style={{ fontSize: 18, color: '#2563eb' }} />Cases per day</div>
              {daily.length === 0 ? (
                <div style={{ color: 'var(--dark-4, #7D8698)', fontSize: 14, padding: '8px 0' }}>No cases submitted in this period.</div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140, overflowX: 'auto', paddingTop: 8 }}>
                  {daily.map((d) => (
                    <div key={d.date} title={`${d.date}: ${d.cases}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 34 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--dark-2, #293855)' }}>{d.cases}</span>
                      <div style={{ width: 20, height: Math.max(4, Math.round((d.cases / maxDay) * 100)), background: 'var(--primary, #213D69)', borderRadius: 4 }} />
                      <span style={{ fontSize: 10.5, color: 'var(--dark-4, #7D8698)' }}>{d.date.slice(5)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ ...card, flex: 1, minWidth: 340, padding: '16px 20px 20px' }}>
              <div style={{ ...cardTitle, marginBottom: 14 }}><ReportProblemOutlined style={{ fontSize: 18, color: '#B3650F' }} />Common error types</div>
              {errorTypes.length === 0 ? (
                <div style={{ color: 'var(--dark-4, #7D8698)', fontSize: 14, padding: '8px 0' }}>No errors logged in this period.</div>
              ) : (
                errorTypes.map((e) => (
                  <div key={e.name} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--dark-2, #293855)', marginBottom: 4 }}><span>{e.name}</span><span style={{ fontWeight: 600 }}>{e.count}</span></div>
                    <div style={{ height: 8, borderRadius: 4, background: 'var(--dark-7, #ECECF0)' }}><div style={{ width: `${Math.round((e.count / maxErr) * 100)}%`, height: '100%', borderRadius: 4, background: '#B3650F' }} /></div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div style={subS}>{productivity.from && productivity.to ? `Showing ${toYmd(new Date(productivity.from))} to ${toYmd(new Date(productivity.to))}` : ''}</div>
        </div>
      )}
    </NavigationLayout>
  );
}

export default ChartAbstractionProductivity;
